import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch, ApiClientError } from "../lib/apiClient";
import { YouTubeEmbed } from "../components/webinar/YouTubeEmbed";
import { QuestionForm } from "../components/webinar/QuestionForm";
import { ConsentGate } from "../components/webinar/ConsentGate";
import { PrivacyNotice } from "../components/webinar/PrivacyNotice";
import type { VerifyResponse } from "../types";

type State =
  | { kind: "loading" }
  | { kind: "invalid" }
  | { kind: "error" }
  | { kind: "ready"; data: VerifyResponse; rid: string };

export function WatchPage() {
  const [params] = useSearchParams();
  const rid = params.get("rid");
  const [state, setState] = useState<State>({ kind: "loading" });
  // 視聴入場時の同意。verifyの結果 or このセッションでの同意で true。
  const [consentedLocal, setConsentedLocal] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!rid) {
      setState({ kind: "invalid" });
      return;
    }
    setState({ kind: "loading" });
    apiFetch<VerifyResponse>(
      `/api/public/registrations/verify?rid=${encodeURIComponent(rid)}`,
    )
      .then((data) => {
        if (!cancelled) setState({ kind: "ready", data, rid });
      })
      .catch((err) => {
        if (cancelled) return;
        // Only a genuine NOT_FOUND means the URL is invalid (doc §12.1).
        // Temporary failures (5xx / network) must not push a valid viewer to
        // the "invalid URL" screen.
        if (err instanceof ApiClientError && err.code === "NOT_FOUND") {
          setState({ kind: "invalid" });
        } else {
          setState({ kind: "error" });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [rid]);

  if (state.kind === "loading") {
    return <div className="centered-message">読み込み中...</div>;
  }

  if (state.kind === "invalid") {
    return (
      <div className="centered-message invalid-url">
        <h1>この視聴URLは無効です。</h1>
        <p>URLが正しいかご確認ください。</p>
        <p>申込時のメールに記載されたURLから再度アクセスしてください。</p>
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="centered-message">
        <h1>読み込みに失敗しました</h1>
        <p>一時的なエラーの可能性があります。しばらくしてから再度お試しください。</p>
        <button
          type="button"
          className="btn-secondary"
          onClick={() => window.location.reload()}
        >
          再読み込み
        </button>
      </div>
    );
  }

  const { webinar, registration } = state.data;
  const consented = registration.consented || consentedLocal;

  return (
    <div className="watch-page">
      <header className="watch-header">
        <div className="logo">Webinar</div>
        <span className="live-badge">LIVE</span>
      </header>

      <section className="watch-title">
        <h1>{webinar.title}</h1>
        {webinar.description && <p>{webinar.description}</p>}
      </section>

      <div className="watch-main">
        <div className="watch-video">
          <YouTubeEmbed videoId={webinar.youtube_video_id} title={webinar.title} />
        </div>
        <aside className="watch-sidebar">
          {consented ? (
            <QuestionForm registrationToken={state.rid} />
          ) : (
            <ConsentGate
              rid={state.rid}
              notice={webinar.privacy_notice}
              onConsented={() => setConsentedLocal(true)}
            />
          )}
        </aside>
      </div>

      <PrivacyNotice notice={webinar.privacy_notice} />
    </div>
  );
}
